/**
 * BranchAvailabilityBadge: a product that cannot be ordered at this branch,
 * and where the shopper can get it instead (branch operations, 4 Oct 2026).
 * ─────────────────────────────────────────────────────────────────────────────
 * Renders nothing for a product that can be ordered here (or a read from a
 * server that predates branches). Otherwise:
 *
 *   [Sold out today at Kawempe]
 *   [Get it from Yalelo Ntinda]  1.2 km away
 *
 * or, when no branch has it, "Not available at any Yalelo branch right now".
 * The nearest branch is utils/branchAvailability.nearestLocationWith: open
 * branches that deliver to the shopper rank first, the same order as the
 * location picker. `onGetItFrom` is the page's own branch switch (the brand
 * storefront's chooseLocation moves the cart along); the cart check then
 * runs at the new branch.
 *
 * All copy comes from utils/branchAvailability, the same words as the apps.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import React from "react";
import styled from "styled-components";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import {
  REASONS,
  branchAlternative,
  branchStateOf,
  distanceAwayLabel,
  getItFromLabel,
  notAnywhereLabel,
  soldOutLabel,
} from "../../utils/branchAvailability";
import { openNowOf } from "../../utils/vendorOpenState";
import { locationName } from "../LocationPicker";

const TONES = {
  [REASONS.SOLD_OUT]: { background: "#FEF2F2", color: "#B91C1C", border: "#FECACA" },
  default: { background: "#F3F4F6", color: "#374151", border: "#E5E7EB" },
};

/**
 * @param {object}   props
 * @param {object}   props.product
 * @param {string}   [props.branchName]  the branch the product was read at
 * @param {object[]} [props.locations]   utils/vendorOutlets.vendorLocationsOf(vendor, ...)
 * @param {string}   [props.vendorName]
 * @param {Function} [props.onGetItFrom] (location) => void
 * @param {Function} [props.isOpen]      location => true | false | null
 * @param {Function} [props.reaches]     location => true | false | null
 * @param {boolean}  [props.compact]     a product card: smaller, one line each
 * @param {boolean}  [props.showLabel]   false when the card already shows the
 *                                       label (a restaurant dish's photo badge)
 * @param {string}   [props.accent]      the storefront's colour for the button
 */
const BranchAvailabilityBadge = ({
  product,
  branchName,
  locations,
  vendorName = "",
  onGetItFrom,
  isOpen = openNowOf,
  reaches,
  compact = false,
  showLabel = true,
  accent = "#2563EB",
}) => {
  const state = branchStateOf(product);
  if (state.orderable) return null;
  const label = soldOutLabel(state, branchName);
  const tone = TONES[state.reason] || TONES.default;
  const alternative = branchAlternative(product, locations, { isOpen, reaches });
  const away = alternative?.kind === "nearest" ? distanceAwayLabel(alternative.location) : null;

  return (
    <Wrap $compact={compact} onClick={(event) => event.stopPropagation()}>
      {showLabel ? (
        <Badge
          role="status"
          $compact={compact}
          style={{ background: tone.background, color: tone.color, borderColor: tone.border }}
        >
          {label}
        </Badge>
      ) : null}
      {alternative?.kind === "nearest" && typeof onGetItFrom === "function" ? (
        <Alternative>
          <GetItFrom
            type="button"
            $compact={compact}
            style={{ color: accent, borderColor: accent }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onGetItFrom(alternative.location);
            }}
          >
            <StorefrontOutlinedIcon style={{ fontSize: compact ? 14 : 16 }} aria-hidden="true" />
            {getItFromLabel(locationName(alternative.location, vendorName))}
          </GetItFrom>
          {away ? <Away>{away}</Away> : null}
        </Alternative>
      ) : null}
      {alternative?.kind === "none" ? <Nowhere>{notAnywhereLabel(vendorName)}</Nowhere> : null}
    </Wrap>
  );
};

export default BranchAvailabilityBadge;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${(p) => (p.$compact ? "4px" : "8px")};
  margin: ${(p) => (p.$compact ? "4px 0" : "8px 0")};
  min-width: 0;
`;

/* Up to two lines, as in the apps: "Sold out today at Yalelo Kawempe
   Market" cut to "Sold out today at Yal..." on a phone hid the one word
   that matters, the branch. */
const Badge = styled.span`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  max-width: 100%;
  padding: ${(p) => (p.$compact ? "2px 8px" : "4px 10px")};
  border: 1px solid transparent;
  border-radius: 8px;
  font-size: ${(p) => (p.$compact ? "11px" : "13px")};
  font-weight: 600;
  line-height: 1.4;
  overflow: hidden;
  white-space: normal;
  overflow-wrap: anywhere;
`;

const Alternative = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const GetItFrom = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: ${(p) => (p.$compact ? "3px 8px" : "6px 12px")};
  border: 1px solid;
  border-radius: 8px;
  background: #ffffff;
  font-size: ${(p) => (p.$compact ? "11px" : "13px")};
  font-weight: 600;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  &:hover {
    opacity: 0.88;
  }
`;

const Away = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

const Nowhere = styled.span`
  font-size: 12px;
  color: #6b7280;
`;
